import {
  CodeOutlined,
  EditOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import { Alert, Box, CircularProgress, Divider } from "@mui/material";
import { isEmpty, isNil } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { blueGrey, grey } from "@mui/material/colors";
import { useFormState } from "react-final-form";
import FlxGrid from "../../../../_src_shared/components/layout/FlxGrid";
import PrimaryContentCard from "../../../../_src_shared/components/layout/PrimaryContentCard";
import PdfDownloadSection from "../../../../_src_shared/components/pdf/PdfDownloadSection";
import LendmarqLoanTermsSheetPDF, {
  mapLoanDataToLetterOfIntent,
} from "../../../../_src_shared/pdf/LendmarqLoanTermsSheetPDF/LendmarqLoanTermsSheetPDF";
import JsonPreview from "../../../../components/common/JsonPreview";
import RffForm from "../../../../components/finalForm/RffForm";
import RffFieldsRenderer from "../../../../components/finalForm/shared/RffFieldsRenderer";
import RffGroup from "../../../../components/finalForm/shared/RffGroup";
import Flx from "../../../../components/layout/Flx";
import PdfPreviewer from "../../../../components/pdf/components/PdfPreviewer";
import Txt from "../../../../components/typography/Txt";
import { useLoanTermsHook } from "../../../../hooks/useLoanTermsHook";
import { loanFetchedDataLoanTermsSet } from "../../../../redux/reducers/loanFetchedDataReducer";
import EnhancedTabSwitcher from "../../../../components/navigation/EnhancedTabSwitcher";

export const LETTER_OF_INTENT_TABS = {
  PREVIEW: "Preview",
  EDIT: "Edit",
  PDF_JSON: "Pdf JSON",
  LOAN_JSON: "Loan JSON",
};
const LoanLetterOfIntentCard = () => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const loanTerms = useSelector((state) => state?.loanFetchedData?.loanTerms);

  const [tabOverride, setTabOverride] = useState(LETTER_OF_INTENT_TABS.PREVIEW);
  return (
    <PrimaryContentCard
      headerSx={{ pb: "0 !important" }}
      maxWidth={tabOverride === LETTER_OF_INTENT_TABS.PREVIEW ? "md" : "xl"}
      title={"Letter of Intent"}
    >
      <LoanLetterOfIntentEditor
        loanData={loanDrilldown}
        loanTerms={loanTerms}
        tabOverride={tabOverride}
        setTabOverride={setTabOverride}
      />
    </PrimaryContentCard>
  );
};
export const LoanLetterOfIntentEditor = ({
  tabOverride,
  setTabOverride,
  loanData,
  loanTerms: loanTermsProp,
  startContent,
  endContent,
  headerEndPrefixContent,
  headerEndSuffixContent,
  hideEditPreview = false,
}) => {
  const { loading, getLoanTerms } = useLoanTermsHook();
  const dispatch = useDispatch();

  const [localTab, setLocalTab] = useState(LETTER_OF_INTENT_TABS.PREVIEW);
  const [localLoanTerms, setLocalLoanTerms] = useState(null);

  const loanTerms = loanTermsProp ?? localLoanTerms;

  const tab = tabOverride ?? localTab;
  const setTab = setTabOverride ?? setLocalTab;

  const formInitialValues = useMemo(() => {
    return mapLoanDataToLetterOfIntent({
      loanTerms,
      formData: loanData,
    });
  }, [loanData, loanTerms]);

  useEffect(() => {
    if (isNil(loanData) || isNil(loanData?._id)) return;
    if (isNil(loanTerms)) {
      getLoanTerms({
        dealId: loanData?._id,
        onSuccessFn: (data) => {
          setLocalLoanTerms(data);
          dispatch(loanFetchedDataLoanTermsSet(data));
        },
        onFailFn: () => {
          setLocalLoanTerms({});
          dispatch(loanFetchedDataLoanTermsSet({}));
        },
      });
    }
  }, [loanTerms, loanData]);

  return (
    <RffForm suppressFooter initialValues={formInitialValues}>
      <FormContent
        tab={tab}
        setTab={setTab}
        loan={loanData}
        loading={loading || isNil(loanTerms)}
        endContent={endContent}
        startContent={startContent}
        headerEndPrefixContent={headerEndPrefixContent}
        headerEndSuffixContent={headerEndSuffixContent}
        hideEditPreview={hideEditPreview}
      />
    </RffForm>
  );
};

const FormContent = ({
  tab,
  setTab,
  endContent,
  startContent,
  headerEndPrefixContent,
  headerEndSuffixContent,
  hideEditPreview,
  loading,
  loan,
}) => {
  const { values } = useFormState();

  let content = null;

  const tabs = useMemo(() => {
    return [
      {
        label: LETTER_OF_INTENT_TABS.PREVIEW,
        value: LETTER_OF_INTENT_TABS.PREVIEW,
        icon: <VisibilityOutlined sx={{ fontSize: "15px" }} />,
      },
      {
        label: LETTER_OF_INTENT_TABS.EDIT,
        value: LETTER_OF_INTENT_TABS.EDIT,
        icon: <EditOutlined sx={{ fontSize: "15px" }} />,
      },
      {
        label: LETTER_OF_INTENT_TABS.PDF_JSON,
        value: LETTER_OF_INTENT_TABS.PDF_JSON,
        icon: <CodeOutlined sx={{ fontSize: "15px" }} />,
      },
      {
        label: LETTER_OF_INTENT_TABS.LOAN_JSON,
        value: LETTER_OF_INTENT_TABS.LOAN_JSON,
        icon: <CodeOutlined sx={{ fontSize: "15px" }} />,
      },
    ];
  }, []);
  let previewContent = (
    <PdfPreviewer
      sx={{
        height: "90vh",
        width: "100%",
        minHeight: "880px",
      }}
    >
      <LendmarqLoanTermsSheetPDF convertedData={values} />
    </PdfPreviewer>
  );

  if (tab === LETTER_OF_INTENT_TABS.EDIT) {
    content = <TermsSheetFormQuestionsGrouped />;
  }
  if (tab === LETTER_OF_INTENT_TABS.PDF_JSON) {
    content = <JsonPreview defaultCollapse={2} values={values} />;
  }
  if (tab === LETTER_OF_INTENT_TABS.LOAN_JSON) {
    content = <JsonPreview defaultCollapse={2} values={loan} />;
  }

  if (loading) {
    return (
      <Flx
        center
        sx={{
          py: 3,
          flexGrow: 1,
          px: 3,
          overflow: "auto",
          border: `1px solid ${grey[300]}`,
          borderRadius: 2,
        }}
      >
        <CircularProgress />
      </Flx>
    );
  }

  return (
    <>
      {startContent}
      <Flx
        ac
        jb
        g={1}
        wrap
        sx={{
          mb: 0.4,
          // borderBottom: "2px solid #e2e8f0",
        }}
      >
        <EnhancedTabSwitcher value={tab} onChange={setTab} tabs={tabs} />
        <Flx ac gap={1}>
          {headerEndPrefixContent}
          <PdfDownloadSection
            variant="button"
            document={<LendmarqLoanTermsSheetPDF />}
            convertedData={values}
            filename="loan-terms.pdf"
          />
          {headerEndSuffixContent}
        </Flx>
      </Flx>

      <Flx fw g={4} sx={{ alignItems: "flex-start" }}>
        {tab === LETTER_OF_INTENT_TABS.PREVIEW ? null : (
          <Box
            sx={{
              py: 3,
              flexGrow: 1,
              px: 3,
              overflow: "auto",
              border: `1px solid ${grey[300]}`,
              borderRadius: 2,
            }}
          >
            {content}
          </Box>
        )}
        {hideEditPreview && tab !== LETTER_OF_INTENT_TABS.PREVIEW ? null : (
          <Box
            sx={{
              overflow: "hidden",
              border: `1px solid ${grey[300]}`,
              borderRadius: 2,
              height: "100%",
              flexBasis: "580px",
              flexShrink: 0,
              flexGrow: 1,
            }}
          >
            {previewContent}
          </Box>
        )}
      </Flx>

      {endContent}
    </>
  );
};

const RffRenderedEntity = () => {
  const { values } = useFormState();

  return (
    <Flx column g={0}>
      <Txt fontSize={14} bold>
        Entity:
      </Txt>
      <Flx column g={1.5}>
        <Txt
          fontSize={12}
          sx={
            isNil(values?.entityName)
              ? { color: "text.secondary", fontStyle: "italic" }
              : undefined
          }
        >
          {values?.entityName ||
            "No entity has been assigned to this loan. You can set one in the Loan Entity tab."}
        </Txt>
        {/* {isNil(values?.entityName) && (
          <LoanDrilldownNavigationButton path="entity">
            Assign Entity
          </LoanDrilldownNavigationButton>
        )} */}
      </Flx>
    </Flx>
  );
};

const RffRenderedGuarantors = () => {
  const { values } = useFormState();

  return (
    <Flx column g={0}>
      <Txt fontSize={14} bold>
        Guarantors:
      </Txt>
      <Flx column g={1.5}>
        {isEmpty(values?.guarantorNames) ? (
          <Txt
            fontSize={12}
            sx={{ color: "text.secondary", fontStyle: "italic" }}
          >
            No guarantors assigned to this loan. Add them from the Loan Entity
            tab or in the borrower data.
          </Txt>
        ) : (
          values?.guarantorNames?.map((name, idx) => (
            <Txt fontSize={12} key={idx}>
              {name}
            </Txt>
          ))
        )}
        {/* {isEmpty(values?.guarantorNames) && (
          <LoanDrilldownNavigationButton path="entity">
            Assign Guarantors
          </LoanDrilldownNavigationButton>
        )} */}
      </Flx>
    </Flx>
  );
};
const RffRenderedProperties = () => {
  const { values } = useFormState();
  return (
    <Flx column g={0}>
      <Txt fontSize={14} bold>
        Properties:
      </Txt>
      {isEmpty(values?.propertyAddresses) ? (
        <Txt fontSize={12}>No properties set for this loan</Txt>
      ) : (
        values?.propertyAddresses?.map((address, idx) => (
          <Txt fontSize={12} key={idx}>
            {address}
          </Txt>
        ))
      )}
    </Flx>
  );
};


const TermsSheetFormQuestionsGrouped = ({ disabled }) => {
  const COL_SIZE = 4;

  // --- Editable Loan Inputs ---
  const loanDetailsFields = useMemo(
    () => [
      {
        name: "loanProductType",
        label: "Loan Product Type",
        type: "text",
        size: COL_SIZE,
      },
      {
        name: "loanPurpose",
        label: "Loan Purpose",
        type: "text",
        size: COL_SIZE,
      },
      {
        name: "exitStrategy",
        label: "Exit Strategy",
        type: "text",
        size: COL_SIZE,
      },
      { name: "loanTerm", label: "Loan Term", type: "text", size: COL_SIZE },
      {
        name: "interestRate",
        label: "Interest Rate",
        type: "percent",
        size: COL_SIZE,
      },
      { name: "occupancy", label: "Occupancy", type: "text", size: COL_SIZE },
      {
        name: "lienPosition",
        label: "Lien Position",
        type: "text",
        size: COL_SIZE,
      },
      {
        name: "paymentReserve",
        label: "Payment Reserve",
        type: "text",
        size: COL_SIZE,
      },
    ],
    [],
  );

  // --- Auto-Calculated (Read-Only) ---
  const loanAmountsFields = useMemo(
    () => [
      {
        name: "totalLoanAmount",
        label: "Total Loan Amount",
        type: "dollar",
        size: COL_SIZE,
      },
      {
        name: "initialLoanAmount",
        label: "Initial Loan Amount",
        type: "dollar",
        size: COL_SIZE,
      },
      {
        name: "purchasePrice",
        label: "Purchase Price",
        type: "dollar",
        size: COL_SIZE,
      },
      {
        name: "estimatedAsIsValue",
        label: "Estimated As-Is Value",
        type: "dollar",
        size: COL_SIZE,
      },
      {
        name: "estimatedArv",
        label: "Estimated ARV",
        type: "dollar",
        size: COL_SIZE,
      },
      {
        name: "loanToCost",
        label: "Loan to Cost",
        type: "percent",
        size: COL_SIZE,
      },
      {
        name: "loanToValue",
        label: "Loan to Value",
        type: "percent",
        size: COL_SIZE,
      },
      {
        name: "estimatedArvLtv",
        label: "Estimated ARV LTV",
        type: "percent",
        size: COL_SIZE,
      },
    ],
    [],
  );

  const constructionFields = useMemo(
    () => [
      {
        name: "constructionBudget",
        label: "Construction Budget",
        type: "dollar",
        size: 6,
      },
      {
        name: "constructionHoldback",
        label: "Construction Holdback",
        type: "dollar",
        size: 6,
      },
    ],
    [],
  );

  const repaymentFields = useMemo(
    () => [
      {
        name: "interestType",
        label: "Interest Type",
        type: "text",
        size: 6,
      },
      {
        name: "amortization",
        label: "Amortization",
        type: "text",
        size: 6,
      },
    ],
    [],
  );

  const feesFields = useMemo(
    () => [
      {
        name: "originationFee",
        label: "Origination Fee",
        type: "dollar",
        size: 4,
      },
      { name: "legalFee", label: "Legal Fee", type: "dollar", size: 4 },
      {
        name: "underwritingFee",
        label: "Underwriting Fee",
        type: "dollar",
        size: 4,
      },
    ],
    [],
  );

  return (
    <Flx column g={3}>
      <Alert severity="info">
        Changes made here are for the term sheet PDF only and will not update
        the loan record.
      </Alert>
      <FlxGrid
        width="140px"
        grow={1}
        gap={3}
        sx={{ background: blueGrey[50], p: 2, borderRadius: 2 }}
      >
        <RffRenderedEntity />
        <RffRenderedGuarantors />
        <RffRenderedProperties />
      </FlxGrid>

      <Box sx={{ p: 1.5 }}>
        <Section fields={loanDetailsFields} label="Loan Details" />
      </Box>

      <Divider />
      <Box sx={{ p: 1.5 }}>
        <Section fields={loanAmountsFields} label="Loan Amounts" />
      </Box>
      <Divider />
      <Box sx={{ p: 1.5 }}>
        <Section fields={feesFields} label={"Fees"} />
      </Box>
      <Divider />
      <Box sx={{ p: 1.5 }}>
        <Section
          label="Construction"
          fields={constructionFields}
          disabled={disabled}
        />
      </Box>
      <Divider />
      <Box sx={{ p: 1.5 }}>
        <Section
          label="Repayment Structure"
          fields={repaymentFields}
          disabled={disabled}
        />
      </Box>
    </Flx>
  );
};

const Section = ({ children, label, fields, disabled }) => {
  return (
    <Flx column g={1}>
      <Txt fontSize={14} bold>
        {label}
      </Txt>
      <RffGroup suppressBottomMargin>
        <RffFieldsRenderer fields={fields} disabled={disabled} />
      </RffGroup>
    </Flx>
  );
};


export const LetterOfIntentFormQuestions = ({ children, columnSize = 6 }) => {
  const letColumnFields = useMemo(
    () => [
      // Left Column
      {
        size: columnSize,
        name: "lienPosition",
        label: "Lien",
        type: "text",
      },
      {
        size: columnSize,
        name: "propertyType",
        label: "Property Type",
        type: "text",
      },
      {
        size: columnSize,
        name: "purchasePrice",
        label: "Purchase Price",
        type: "dollar",
      },
      {
        size: columnSize,
        name: "constructionBudget",
        label: "Construction Budget",
        type: "dollar",
      },
      {
        size: columnSize,
        name: "estimatedAsIsValue",
        label: "Estimated As-Is Value",
        type: "dollar",
      },
      {
        size: columnSize,
        name: "estimatedArv",
        label: "Estimated ARV",
        type: "dollar",
      },
      {
        size: columnSize,
        name: "occupancy",
        label: "Occupancy",
        type: "text",
      },
      {
        size: columnSize,
        name: "exitStrategy",
        label: "Exit Strategy",
        type: "text",
      },
      {
        size: columnSize,
        name: "totalLoanAmount",
        label: "Total Loan Amount",
        type: "dollar",
      },
      {
        size: columnSize,
        name: "initialLoanAmount",
        label: "Initial Loan Amount",
        type: "dollar",
      },
      {
        size: columnSize,
        name: "constructionHoldback",
        label: "Construction Holdback",
        type: "dollar",
      },
    ],
    [],
  );
  const rightColumnFields = useMemo(
    () => [
      // Right Column
      {
        size: columnSize,
        name: "loanTerm",
        label: "Loan Term",
        type: "text",
      },
      {
        size: columnSize,
        name: "interestRate",
        label: "Interest Rate",
        type: "percent",
      },
      {
        size: columnSize,
        name: "interestType",
        label: "Interest Type",
        type: "text",
      },
      {
        size: columnSize,
        name: "amortization",
        label: "Amortization",
        type: "text",
      },
      {
        size: columnSize,
        name: "originationFee",
        label: "Origination Fee",
        type: "dollar",
      },
      {
        size: columnSize,
        name: "legalFee",
        label: "Legal Fee",
        type: "dollar",
      },
      {
        size: columnSize,
        name: "underwritingFee",
        label: "Underwriting Fee",
        type: "dollar",
      },
      {
        size: columnSize,
        name: "paymentReserve",
        label: "Payment Reserve",
        type: "text",
      },
      {
        size: columnSize,
        name: "loanToCost",
        label: "Loan to Cost",
        type: "percent",
      },
      {
        size: columnSize,
        name: "loanToValue",
        label: "Loan to Value",
        type: "percent",
      },
      {
        size: columnSize,
        name: "estimatedArvLtv",
        label: "Estimated ARV LTV",
        type: "percent",
      },
    ],
    [],
  );
  return (
    <FlxGrid gap={4}>
      <RffGroup>
        <RffFieldsRenderer fields={letColumnFields} />
      </RffGroup>
      <RffGroup>
        <RffFieldsRenderer fields={rightColumnFields} />
      </RffGroup>
    </FlxGrid>
  );
};


export default LoanLetterOfIntentCard;
